import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Select, { ValueType } from 'react-select';
import NumberFormat from 'react-number-format';
import EdiText from 'react-editext';
import ArrowForward from '@material-ui/icons/ArrowForward';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { Button as MIButton } from '@material-ui/core';
import moment from 'moment';

import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { PaymentType } from '../../../../../shared/enums/payment-type.enum';
import { PaymentCollector } from '../../../../../shared/enums/payment-collector.enum';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  PaymentUIModel,
  SplitPaymentModel
} from '../../../../../shared/ui-models/payment.ui-model';
import { PaymentOccurrenceMode } from '../../../../../shared/enums/payment-occurrence-mode.enum';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import {
  checkPaymobPaymentStatus,
  createOnePayment,
  generatePaymentLink,
  sendPayment,
  uploadPaymentReceipt,
  updateOneReservations,
  updateOnePayment
} from '../../../../shared/services/data.service';
import { trackPurchase } from '../../../../../shared/services/utils/tracking/tracking.service';
import {
  collectorOption,
  paymentStatusOptions,
  paymentTypeOptions,
  getPaymentStatusColor
} from '../../reservation-edit.helper';
import { AddPaymentModal } from '../add-payment-modal/add-payment-modal.component';
import { SplitPaymentModal } from '../split-payment-modal/split-payment-modal.component';
import { GeneratePaymentLinkModal } from '../generate-payment-link-modal/generate-payment-link-modal.component';
import { SendPaymentLinkModal } from '../send-payment-link-modal/send-payment-link-modal.component';
import { AddPaymentReceiptModal } from '../add-payment-receipt-modal/add-payment-receipt-modal.component';
import { AddPartialPaymentModal } from '../add-partial-payment-modal/add-partial-payment-modal.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { AddInvoiceIcon } from '../../../../../ui/Icons';
import { PaymentHistoryModal } from '../payment-history-modal/payment-history-modal.component';

// ---------------------------------------------------------------------------
// Helper: derive payment method string from attempts or paymentType
// ---------------------------------------------------------------------------
function getPaymentMethod(payment: PaymentModel): string {
  if (payment.attempts && payment.attempts.length > 0) {
    // Try to find the latest attempt with a card number
    for (let i = payment.attempts.length - 1; i >= 0; i--) {
      const attempt = payment.attempts[i];
      if (attempt.cardNumber) {
        return attempt.cardNumber;
      }
    }
  }
  // Fallback to payment type label
  const typeOption = paymentTypeOptions.find((opt) => opt.value === payment.paymentType);
  return typeOption ? typeOption.label : payment.paymentType || 'N/A';
}

function getDescription(payment: PaymentModel): string {
  if (payment.paymentItems && payment.paymentItems.length > 0) {
    const names = payment.paymentItems.filter((item) => item.feeName).map((item) => item.feeName);
    if (names.length > 0) {
      return names.length > 2 ? `${names[0]}, ${names[1]}...` : names.join(', ');
    }
  }
  return payment.direction === 'out' ? 'Refund' : 'Payment';
}

function getPaidDate(payment: PaymentModel): string {
  if (payment.collectedAt) {
    return moment(payment.collectedAt).format('D MMM, YYYY');
  }
  if (payment.dueDate) {
    return moment(payment.dueDate).format('D MMM, YYYY');
  }
  return 'N/A';
}

// ---------------------------------------------------------------------------
// StatusBadge component
// ---------------------------------------------------------------------------
interface StatusBadgeProps {
  status: PaymentStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  let label = '';
  let dotColor = '';
  let bgColor = '';
  let textColor = '';
  let borderColor = '';

  switch (status) {
    case PaymentStatus.COLLECTED:
      label = 'Done';
      dotColor = '#06A458';
      bgColor = '#ecfdf3';
      textColor = '#06A458';
      borderColor = '#a6f4c5';
      break;
    case PaymentStatus.PENDING:
      label = 'Pending';
      dotColor = '#F79009';
      bgColor = '#fffbeb';
      textColor = '#F79009';
      borderColor = '#fde68a';
      break;
    case PaymentStatus.OVERDUE:
      label = 'Failed';
      dotColor = '#F04438';
      bgColor = '#fef3f2';
      textColor = '#F04438';
      borderColor = '#fecaca';
      break;
    case PaymentStatus.CANCELED:
      label = 'Canceled';
      dotColor = '#F04438';
      bgColor = '#fef3f2';
      textColor = '#F04438';
      borderColor = '#fecaca';
      break;
    case PaymentStatus.REFUNDED:
      label = 'Refunded';
      dotColor = '#8B5CF6';
      bgColor = '#f5f3ff';
      textColor = '#8B5CF6';
      borderColor = '#ddd6fe';
      break;
    case PaymentStatus.PARTIALLY_COLLECTED:
      label = 'Partial';
      dotColor = '#0EA5E9';
      bgColor = '#f0f9ff';
      textColor = '#0EA5E9';
      borderColor = '#bae6fd';
      break;
    default:
      label = status || 'Unknown';
      dotColor = '#9CA3AF';
      bgColor = '#f3f4f6';
      textColor = '#6B7280';
      borderColor = '#e5e7eb';
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[13px] font-medium ${bgColor} ${textColor} ${borderColor}`}
      style={{ backgroundColor: bgColor, color: textColor, borderColor }}
    >
      <span
        className="shrink-0"
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          backgroundColor: dotColor
        }}
      />
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// ActionDropdown component
// ---------------------------------------------------------------------------
interface ActionDropdownProps {
  payment: PaymentModel;
  isPermited: (roles: UserRole[]) => boolean;
  reservationSource?: string;
  isCheckingPaymobStatus?: boolean;
  onSend: () => void;
  onPartial: () => void;
  onSplitPayment: () => void;
  onGenerateLink: () => void;
  onAddReceipt: () => void;
  onViewHistory: () => void;
  onCheckPaymobStatus: () => void;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  payment,
  isPermited,
  reservationSource,
  isCheckingPaymobStatus = false,
  onSend,
  onPartial,
  onSplitPayment,
  onGenerateLink,
  onAddReceipt,
  onViewHistory,
  onCheckPaymobStatus
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; openUp: boolean }>({
    top: 0,
    left: 0,
    openUp: false
  });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        btnRef.current &&
        !btnRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isNotCollectedOrRefunded =
    payment.status !== PaymentStatus.REFUNDED && payment.status !== PaymentStatus.COLLECTED;

  const canTakeAction =
    isNotCollectedOrRefunded && payment.direction !== 'out' && payment.paymentAmount > 0;

  const hasPaymentLink = !!payment.birdnestPaymentLink;

  const isFinanceRole = isPermited([
    UserRole.FINANCE_MANAGER,
    UserRole.FINANCE_SPECIALIST,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  const isReservationTeamRole = isPermited([
    UserRole.RESERVATIONS,
    UserRole.RESERVATION_MANAGER,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  // Show send only if payment link exists
  const showSend = canTakeAction && hasPaymentLink;

  // Show partial for finance roles when not collected/refunded
  const showPartial = canTakeAction && hasPaymentLink && isFinanceRole;

  // Show split when not collected/refunded, direction !== 'out', amount > 0
  const showSplit =
    reservationSource !== 'channel-manager' &&
    isNotCollectedOrRefunded &&
    payment.direction !== 'out' &&
    payment.paymentAmount > 0;

  // Show generate link when no link exists
  const showGenerateLink = canTakeAction && !hasPaymentLink;

  // Show add receipt when collected/partially collected/refunded
  const showAddReceipt =
    payment.status === PaymentStatus.COLLECTED ||
    payment.status === PaymentStatus.PARTIALLY_COLLECTED ||
    payment.status === PaymentStatus.REFUNDED;

  const showCheckPaymobStatus =
    isReservationTeamRole &&
    payment.id &&
    [PaymentStatus.OVERDUE, PaymentStatus.PENDING, PaymentStatus.CANCELED].includes(payment.status);

  // Show copy link when payment link exists
  const showCopyLink = hasPaymentLink;

  // Show view history for all payments
  const showViewHistory = true;

  const hasAnyAction =
    showSend ||
    showPartial ||
    showSplit ||
    showGenerateLink ||
    showAddReceipt ||
    showCopyLink ||
    showViewHistory ||
    showCheckPaymobStatus;

  if (!hasAnyAction) {
    return <span style={{ width: '24px' }} />;
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            const actionCount = [
              showCopyLink,
              showSend,
              showPartial,
              showSplit,
              showGenerateLink,
              showAddReceipt,
              showCheckPaymobStatus
            ].filter(Boolean).length;
            const estimatedHeight = actionCount * 40 + 8;
            const spaceBelow = window.innerHeight - rect.bottom - 4;
            const openUp = spaceBelow < estimatedHeight && rect.top > estimatedHeight;
            setDropdownPos({
              top: openUp ? rect.top - 4 : rect.bottom + 4,
              left: rect.right,
              openUp
            });
          }
          setOpen(!open);
        }}
        className="flex cursor-pointer items-center justify-center rounded-md border-0 bg-transparent px-2 py-1 text-neutral-500 transition-colors hover:bg-gray-100"
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      {open &&
        ReactDOM.createPortal(
          <div
            ref={ref}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              transform: dropdownPos.openUp ? 'translate(-100%, -100%)' : 'translateX(-100%)',
              minWidth: '170px',
              zIndex: 9999
            }}
          >
            {showCopyLink && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  navigator.clipboard.writeText(payment.birdnestPaymentLink || '').then(() => {
                    birdToast.success('Payment link copied!');
                  });
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left font-gilroy-medium font-semibold text-xl text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                Copy Link
              </button>
            )}
            {showSend && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSend();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                </svg>
                Send
              </button>
            )}
            {showPartial && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onPartial();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
                Partial
              </button>
            )}
            {showSplit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onSplitPayment();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 3h5v5" />
                  <line x1="4" y1="20" x2="21" y2="3" />
                  <path d="M21 16v5h-5" />
                  <line x1="15" y1="15" x2="21" y2="21" />
                  <line x1="4" y1="4" x2="9" y2="9" />
                </svg>
                Split Payment
              </button>
            )}
            {showGenerateLink && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onGenerateLink();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Generate Link
              </button>
            )}
            {showAddReceipt && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onAddReceipt();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                Add Receipt
              </button>
            )}
            {showViewHistory && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onViewHistory();
                }}
                className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold text-neutral-500 transition-colors hover:bg-gray-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                View History
              </button>
            )}
            {showCheckPaymobStatus && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (!isCheckingPaymobStatus) {
                    onCheckPaymobStatus();
                  }
                }}
                disabled={isCheckingPaymobStatus}
                className={`flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3.5 py-2.5 text-left text-xl font-gilroy-medium font-semibold transition-colors hover:bg-gray-50 ${
                  isCheckingPaymobStatus
                    ? 'cursor-not-allowed text-gray-400 opacity-70'
                    : 'text-neutral-500'
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <polyline points="21 3 21 9 15 9" />
                </svg>
                {isCheckingPaymobStatus ? 'Checking...' : 'Check Paymob Status'}
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

interface PaymentDetailsModalProps {
  showModal: boolean;
  payment: PaymentModel | null;
  reservation: ReservationDetailsUiModel;
  isPermited: (roles: UserRole[]) => boolean;
  onClose: () => void;
  onGetOneReservation: (id: string) => Promise<any>;
  onStatusChange: (payment: PaymentModel, newStatus: PaymentStatus) => void;
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({
  showModal,
  payment,
  reservation,
  isPermited,
  onClose,
  onGetOneReservation,
  onStatusChange
}) => {
  if (!payment) return null;

  const paymentStatusColourStyles = (stateValue: any) => {
    return {
      singleValue: (styles: any) => ({ ...styles, color: 'inherit' }),
      control: (styles: any) => {
        const colorObj = getPaymentStatusColor(stateValue);
        return {
          ...styles,
          ...colorObj
        };
      },
      option: (styles: any, { data }: any) => {
        const colorObj = getPaymentStatusColor(data.value);
        return {
          ...styles,
          ...colorObj
        };
      }
    };
  };

  const isEditable =
    payment.paymentAmount > 0 &&
    !(
      payment.status === PaymentStatus.PARTIALLY_COLLECTED &&
      !isPermited([
        UserRole.FINANCE_MANAGER,
        UserRole.FINANCE_SPECIALIST,
        UserRole.ADMIN,
        UserRole.SUPER_ADMIN
      ])
    );

  return (
    <Modal
      config={{ showModal, fullScreen: false, showClose: true, maxWidth: 'lg' }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div style={{ padding: '24px' }}>
        <h3
          style={{
            margin: '0 0 20px 0',
            fontSize: '18px',
            fontWeight: 600,
            color: '#111827'
          }}
        >
          Payment Details - {payment.id}
        </h3>

        {/* Payment info summary */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px'
          }}
        >
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Status
            </div>
            {payment.paymentAmount > 0 ? (
              <Select
                styles={paymentStatusColourStyles(payment.status)}
                isDisabled={payment.paymentAmount <= 0}
                options={
                  payment.direction === 'out'
                    ? [{ label: 'Refunded', value: PaymentStatus.REFUNDED }]
                    : [
                        { label: 'Paid', value: PaymentStatus.COLLECTED },
                        ...(isPermited([
                          UserRole.FINANCE_MANAGER,
                          UserRole.FINANCE_SPECIALIST,
                          UserRole.ADMIN,
                          UserRole.SUPER_ADMIN
                        ])
                          ? [
                              {
                                label: 'Partially Collected',
                                value: PaymentStatus.PARTIALLY_COLLECTED
                              }
                            ]
                          : [])
                      ]
                }
                defaultValue={paymentStatusOptions.find((val) => val.value === payment.status)}
                onChange={(
                  value: ValueType<{ value: string | undefined; label: string }, false>
                ) => {
                  const newStatus =
                    (value && (value.value as PaymentStatus)) || PaymentStatus.COLLECTED;
                  onStatusChange(payment, newStatus);
                }}
              />
            ) : (
              <StatusBadge status={payment.status} />
            )}
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Amount
            </div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
              <NumberFormat
                value={payment.paymentAmount || 0}
                displayType={'text'}
                thousandSeparator={true}
                prefix={payment.currency ? CURRENCY[payment.currency] + ' ' : 'EGP '}
              />
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Direction
            </div>
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>
              {payment.direction === 'out' ? 'PAY-OUT' : 'PAY-IN'}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Due Date
            </div>
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>
              {new Date(payment.dueDate).toDateString()}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Type
            </div>
            <Select
              className="min-w-[11rem]"
              isDisabled={!isEditable}
              options={paymentTypeOptions}
              defaultValue={paymentTypeOptions.find((val) => val.value === payment.paymentType)}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                updateOnePayment({
                  id: payment.id,
                  paymentType: value ? (value.value as PaymentType) : payment.paymentType
                }).then(() => onGetOneReservation(reservation.id));
              }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}
            >
              Collector
            </div>
            <Select
              className="min-w-[11rem]"
              isDisabled={!isEditable}
              options={collectorOption}
              defaultValue={collectorOption.find((val) => val.value === payment.collector)}
              onChange={(value: ValueType<{ value: string | undefined; label: string }, false>) => {
                updateOnePayment({
                  id: payment.id,
                  collector: value ? (value.value as PaymentCollector) : payment.collector
                }).then(() => onGetOneReservation(reservation.id));
              }}
            />
          </div>
        </div>

        {/* Updated info */}
        {(payment.status === PaymentStatus.COLLECTED ||
          payment.status === PaymentStatus.PARTIALLY_COLLECTED ||
          payment.status === PaymentStatus.REFUNDED) && (
          <div
            style={{
              marginBottom: '24px',
              padding: '12px 16px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            {payment.updatedBy && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Updated by: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>{payment.updatedBy}</span>
              </div>
            )}
            {payment.updatedAt && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Updated at: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>
                  {moment(payment.updatedAt).format('DD/MM/YYYY, h:mm:ss a')}
                </span>
              </div>
            )}
            {payment.status === PaymentStatus.PARTIALLY_COLLECTED && payment.partialPayment && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Partial Amount: </span>
                <span style={{ fontWeight: 600, color: '#111827' }}>
                  <NumberFormat
                    value={payment.partialPayment.amount || 0}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={
                      payment.partialPayment.currency
                        ? CURRENCY[payment.partialPayment.currency as any] + ' '
                        : 'EGP '
                    }
                  />
                </span>
              </div>
            )}
            {(payment.partialPayment || payment.bankCharges) && (
              <>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#6B7280' }}>Bank Charges: </span>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    <NumberFormat
                      value={(payment.partialPayment?.bankCharges ?? payment.bankCharges) || 0}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={
                        payment.partialPayment?.bankChargesCurrency || payment.bankChargesCurrency
                          ? CURRENCY[
                              (payment.partialPayment?.bankChargesCurrency ||
                                payment.bankChargesCurrency) as any
                            ] + ' '
                          : 'EGP '
                      }
                    />
                  </span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#6B7280' }}>Forex Difference: </span>
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    <NumberFormat
                      value={
                        (payment.partialPayment?.forexDifference ?? payment.forexDifference) || 0
                      }
                      displayType={'text'}
                      thousandSeparator={true}
                    />
                  </span>
                </div>
              </>
            )}
            {payment.receipt?.receiptUrl && (
              <div style={{ marginBottom: '4px' }}>
                <span style={{ color: '#6B7280' }}>Proof of Payment: </span>
                <a
                  href={payment.receipt.receiptUrl}
                  style={{ color: '#237c58', textDecoration: 'underline', fontWeight: 600 }}
                >
                  {payment.receipt.receiptId}
                </a>
              </div>
            )}
            {payment.partialPaymentReceipt?.receiptUrl && (
              <div>
                <span style={{ color: '#6B7280' }}>Partial Receipt: </span>
                <a
                  href={payment.partialPaymentReceipt.receiptUrl}
                  style={{ color: '#237c58', textDecoration: 'underline', fontWeight: 600 }}
                >
                  {payment.partialPaymentReceipt.receiptId}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Fee breakdown table */}
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
          Fee Breakdown
        </div>
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              padding: '10px 16px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6B7280',
              textTransform: 'uppercase'
            }}
          >
            <div>Fee Name</div>
            <div>Type</div>
            <div>Amount</div>
          </div>
          {payment.paymentItems && payment.paymentItems.length > 0 ? (
            payment.paymentItems.map((paymentItem, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  padding: '12px 16px',
                  borderBottom:
                    idx < payment.paymentItems.length - 1 ? '1px solid #f3f4f6' : 'none',
                  fontSize: '14px',
                  color: '#374151',
                  alignItems: 'center'
                }}
              >
                <div>
                  {paymentItem.feeName === 'Unit Price' &&
                  paymentItem.feeValue &&
                  paymentItem.duration
                    ? `${paymentItem.feeValue / paymentItem.duration}x${paymentItem.duration}`
                    : paymentItem.feeName}
                </div>
                <div>{paymentItem.itemType || 'N/A'}</div>
                <div>
                  {(payment.status === PaymentStatus.OVERDUE ||
                    payment.status === PaymentStatus.PENDING) &&
                  hasAccess([PERMISSIONS_LIST.paymentsFullAccess]) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <EdiText
                        type="number"
                        value={`${paymentItem.feeValue}`}
                        onSave={(val) => {
                          const updatedPaymentItems = [...payment.paymentItems];
                          updatedPaymentItems[idx] = {
                            ...updatedPaymentItems[idx],
                            feeValue: val
                          };
                          updateOnePayment({
                            id: payment.id,
                            document: updatedPaymentItems
                          }).then(() => onGetOneReservation(reservation.id));
                        }}
                      />
                      <MIButton
                        aria-controls="area-menu"
                        aria-haspopup="true"
                        onClick={() => {
                          const updatedPaymentItems = [...payment.paymentItems];
                          updatedPaymentItems[idx] = {
                            ...updatedPaymentItems[idx],
                            feeValue: 0
                          };
                          updateOnePayment({
                            id: payment.id,
                            document: updatedPaymentItems
                          }).then(() => onGetOneReservation(reservation.id));
                        }}
                      >
                        <DeleteIcon fontSize="small" color={'error'} />
                      </MIButton>
                    </div>
                  ) : (payment.status === PaymentStatus.OVERDUE ||
                      payment.status === PaymentStatus.PENDING) &&
                    hasAccess([PERMISSIONS_LIST.paymentsDeleteAccess]) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <NumberFormat
                        value={paymentItem.feeValue}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={
                          paymentItem.currency ? CURRENCY[paymentItem.currency] + ' ' : 'EGP '
                        }
                      />
                      <MIButton
                        aria-controls="area-menu"
                        aria-haspopup="true"
                        onClick={() => {
                          const updatedPaymentItems = [...payment.paymentItems];
                          updatedPaymentItems[idx] = {
                            ...updatedPaymentItems[idx],
                            feeValue: 0
                          };
                          updateOnePayment({
                            id: payment.id,
                            document: updatedPaymentItems
                          }).then(() => onGetOneReservation(reservation.id));
                        }}
                      >
                        <DeleteIcon fontSize="small" color={'error'} />
                      </MIButton>
                    </div>
                  ) : (
                    <NumberFormat
                      value={paymentItem.feeValue}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={paymentItem.currency ? CURRENCY[paymentItem.currency] + ' ' : 'EGP '}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div
              style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}
            >
              No fee items
            </div>
          )}
        </div>

        {/* Online Attempts */}
        {payment.attempts && payment.attempts.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div
              style={{ fontSize: '14px', fontWeight: 600, color: '#237c58', marginBottom: '12px' }}
            >
              Online Attempts
            </div>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                  padding: '10px 16px',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6B7280',
                  textTransform: 'uppercase'
                }}
              >
                <div>Status</div>
                <div>Card Number</div>
                <div>Attempt Date</div>
                <div>Paymob Order ID</div>
                <div>Reason</div>
              </div>
              {payment.attempts.map((attempt, aIdx) => (
                <div
                  key={aIdx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
                    padding: '10px 16px',
                    borderBottom:
                      aIdx < (payment.attempts?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none',
                    fontSize: '13px',
                    color: '#374151',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <img
                      style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                      src={
                        attempt.attemptStatus === 'completed'
                          ? `${PUBLIC_PATH}/assets/jpeg/payment-card-success.jpeg`
                          : attempt.attemptStatus === 'fraud'
                          ? `${PUBLIC_PATH}/assets/jpeg/danger.jpeg`
                          : `${PUBLIC_PATH}/assets/jpeg/failed-payment.webp`
                      }
                      alt={attempt.attemptStatus}
                    />
                    {attempt.attemptStatus}
                  </div>
                  <div>{attempt.cardNumber || 'N/A'}</div>
                  <div>{new Date(attempt.attemptDate).toDateString()}</div>
                  <div>{attempt.paymobOrderId || 'N/A'}</div>
                  <div>
                    {attempt.reason}
                    {attempt.reason === 'Approved' && payment.targetBankAccount
                      ? ' / ' + payment.targetBankAccount
                      : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main PaymentsSection component
// ---------------------------------------------------------------------------

interface PaymentsSectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
    contactEmail: string;
  };
  config: {
    isPermited: (roles: UserRole[]) => boolean;
    userRole: any;
    userRoles: any;
  };
  eventHandlers: {
    onGetOneReservation: (id: string) => Promise<any>;
  };
}

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({
  data: { reservation, contactEmail },
  config: { isPermited },
  eventHandlers: { onGetOneReservation }
}) => {
  // Modal states
  const [showAddPaymentModal, setShowAddPaymentModal] = useState<boolean>(false);
  const [showGeneratePaymentLinkModal, setShowGeneratePaymentLinkModal] = useState<boolean>(false);
  const [showSplitPaymentModal, setShowSplitPaymentModal] = useState<boolean>(false);
  const [showSendPaymentLinkModal, setShowSendPaymentLinkModal] = useState<boolean>(false);
  const [showAddPaymentRecipetModal, setShowAddPaymentRecipetModal] = useState<boolean>(false);
  const [showAddPartialPaymentModal, setShowAddPartialPaymentModal] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState<boolean>(false);

  const [activePayment, setActivePayment] = useState<PaymentModel>();
  const [activePaymentId, setActivePaymentId] = useState<string>('');
  const [detailPayment, setDetailPayment] = useState<PaymentModel | null>(null);
  const [checkingPaymobPaymentId, setCheckingPaymobPaymentId] = useState<string>('');

  const activePaymentIdForHistory = detailPayment?.id || activePayment?.id || '';

  // Table column styles
  const headerStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 600,
    color: '#6B7280',
    padding: '14px 16px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #e5e7eb'
  };

  const cellStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#111827',
    padding: '16px',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'middle'
  };

  return (
    <>
      {/* ============ MODALS ============ */}
      <AddPaymentModal
        config={{ showModal: showAddPaymentModal }}
        data={{ reservation }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddPaymentModal(false);
          },
          addPaymentHandler: (payment) => {
            createOnePayment(payment)
              .then((response: PaymentUIModel | ErrorInfo) => {
                if (!isErrorInfo(response)) {
                  return response;
                }
              })
              .then((newPayment: PaymentUIModel | undefined) => {
                if (reservation && newPayment) {
                  let updatedReservation: Partial<ReservationDetailsUiModel> = {
                    id: reservation.id
                  };
                  if (Array.isArray(reservation.paymentIds)) {
                    updatedReservation = {
                      ...updatedReservation,
                      paymentIds: [...reservation.paymentIds, newPayment.id || '']
                    };
                  } else {
                    updatedReservation = {
                      ...updatedReservation,
                      paymentIds: [newPayment.id || '']
                    };
                  }
                  updateOneReservations(updatedReservation).then(() =>
                    onGetOneReservation(reservation.id).then(() => setShowAddPaymentModal(false))
                  );
                }
              });
          }
        }}
      />
      <SplitPaymentModal
        config={{ showModal: showSplitPaymentModal }}
        data={{
          reservation,
          sourcePayment: reservation?.paymentList?.find((payment) => payment.id === activePaymentId)
        }}
        eventHandlers={{
          closeHandler: () => {
            setActivePaymentId('');
            setShowSplitPaymentModal(false);
          },
          SplitPaymentHandler: (paymentSplitData: SplitPaymentModel) => {
            updateOnePayment(paymentSplitData.paymentToUpdate).then(() => {
              createOnePayment(paymentSplitData.paymentToAdd)
                .then((response: PaymentUIModel | ErrorInfo) => {
                  if (!isErrorInfo(response)) {
                    return response;
                  }
                })
                .then((newPayment: PaymentUIModel | undefined) => {
                  setShowSplitPaymentModal(false);
                  if (reservation && newPayment) {
                    let updatedReservation: Partial<ReservationDetailsUiModel> = {
                      id: reservation.id
                    };
                    if (Array.isArray(reservation.paymentIds)) {
                      updatedReservation = {
                        ...updatedReservation,
                        paymentIds: [...reservation.paymentIds, newPayment.id || '']
                      };
                    } else {
                      updatedReservation = {
                        ...updatedReservation,
                        paymentIds: [newPayment.id || '']
                      };
                    }
                    updateOneReservations(updatedReservation).then(() =>
                      onGetOneReservation(reservation.id).then(() => setShowAddPaymentModal(false))
                    );
                  }
                });
            });
          }
        }}
      />
      <GeneratePaymentLinkModal
        config={{ showModal: showGeneratePaymentLinkModal }}
        data={{ contactInfo: contactEmail }}
        eventHandlers={{
          closeHandler: () => {
            setActivePaymentId('');
            setShowGeneratePaymentLinkModal(false);
          },
          generatePaymentLinkHandler: ({ skipEmail, email }) => {
            generatePaymentLink({ paymentId: activePaymentId.toString(), skipEmail, email }).then(
              (response: any | ErrorInfo) => {
                if (!isErrorInfo(response)) {
                  birdToast.success('Payment Link Generated Successfully');
                  if (reservation) {
                    onGetOneReservation(reservation.id).then(() => {
                      setActivePaymentId('');
                      setShowGeneratePaymentLinkModal(false);
                    });
                  }
                  return response;
                } else {
                  birdToast.error('Payment Link failed To Generated');
                }
              }
            );
          }
        }}
      />
      <SendPaymentLinkModal
        config={{ showModal: showSendPaymentLinkModal }}
        data={{ contactInfo: contactEmail }}
        eventHandlers={{
          closeHandler: () => {
            setActivePaymentId('');
            setShowSendPaymentLinkModal(false);
          },
          sendPaymentLinkHandler: (email: string) => {
            sendPayment(activePaymentId, email).then((response: any | ErrorInfo) => {
              if (!isErrorInfo(response)) {
                birdToast.success('Payment Link Sent Successfully');
                setShowSendPaymentLinkModal(false);
                return response;
              } else {
                birdToast.error('Payment Link failed To be sent');
              }
            });
          }
        }}
      />
      <AddPaymentReceiptModal
        config={{
          showModal: showAddPaymentRecipetModal,
          userRole: undefined,
          userRoles: undefined
        }}
        data={{ status: activePayment?.status, paymentId: activePayment?.id }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddPaymentRecipetModal(false);
          },
          addPaymentReceiptHandler: (
            asset: File,
            collectedAtDate: Date,
            status?: PaymentStatus,
            id?: string,
            skipEmail?: boolean,
            bankCharges?: number,
            bankChargesCurrency?: string,
            forexDifference?: number
          ) => {
            if (asset && id && reservation) {
              uploadPaymentReceipt(
                asset,
                collectedAtDate,
                id,
                reservation.id,
                status || PaymentStatus.COLLECTED,
                skipEmail || false,
                undefined,
                bankCharges,
                bankChargesCurrency,
                forexDifference
              ).then(() => {
                if (activePayment?.type === PaymentOccurrenceMode.ONE_TIME) {
                  trackPurchase(
                    reservation && reservation.paymentList
                      ? reservation.paymentList
                          .filter((payment) => payment.status === PaymentStatus.COLLECTED)
                          .reduce(
                            (prev, current) =>
                              current.direction === 'out'
                                ? prev - ((current && current.paymentAmount) || 0)
                                : prev + ((current && current.paymentAmount) || 0),
                            0
                          )
                      : 0,
                    'EGP '
                  );
                }
                onGetOneReservation(reservation.id);
                setShowAddPaymentRecipetModal(false);
              });
            }
          }
        }}
      />
      <AddPartialPaymentModal
        config={{ showModal: showAddPartialPaymentModal }}
        data={{
          paymentId: activePayment?.id,
          currency: activePayment?.currency,
          totalAmount: activePayment?.paymentAmount
        }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddPartialPaymentModal(false);
          },
          addPartialPaymentHandler: (
            asset: File,
            partialAmount: number,
            collectedAtDate: Date,
            id?: string,
            skipEmail?: boolean,
            bankCharges?: number,
            bankChargesCurrency?: string,
            forexDifference?: number
          ) => {
            if (asset && id && reservation && partialAmount > 0) {
              uploadPaymentReceipt(
                asset,
                collectedAtDate,
                id,
                reservation.id,
                PaymentStatus.PARTIALLY_COLLECTED,
                skipEmail || false,
                partialAmount,
                bankCharges,
                bankChargesCurrency,
                forexDifference
              ).then(() => {
                onGetOneReservation(reservation.id);
                setShowAddPartialPaymentModal(false);
              });
            }
          }
        }}
      />

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        showModal={showDetailsModal}
        payment={detailPayment}
        reservation={reservation}
        isPermited={isPermited}
        onClose={() => {
          setShowDetailsModal(false);
          setDetailPayment(null);
        }}
        onGetOneReservation={onGetOneReservation}
        onStatusChange={(payment, newStatus) => {
          setActivePayment({
            ...payment,
            status: newStatus
          });
          if (newStatus === PaymentStatus.PARTIALLY_COLLECTED) {
            setShowAddPartialPaymentModal(true);
          } else {
            setShowAddPaymentRecipetModal(true);
          }
        }}
      />

      <PaymentHistoryModal
        config={{ showModal: showPaymentHistoryModal }}
        data={{ paymentId: activePaymentIdForHistory }}
        eventHandlers={{ onClose: () => setShowPaymentHistoryModal(false) }}
      />

      {/* ============ TABLE UI ============ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}
        >
          <span
            style={{
              fontSize: '22px',
              fontWeight: 700,
              color: '#566346'
            }}
          >
            Payments
          </span>

          {hasAccess([
            PERMISSIONS_LIST.reservationsFullAccess,
            PERMISSIONS_LIST.reservationsModifyAccess,
            PERMISSIONS_LIST.paymentsCreateAccess
          ]) && (
            <div
              onClick={() => setShowAddPaymentModal(true)}
              className="cursor-pointer border border-neutral-50 p-2 rounded-full"
            >
              <AddInvoiceIcon size="22px" />
            </div>
          )}
        </div>

        {/* Table */}
        {reservation && reservation.paymentList && reservation.paymentList.length > 0 ? (
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            {/* Left green accent bar */}
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  backgroundColor: '#22c55e',
                  borderTopLeftRadius: '12px',
                  borderBottomLeftRadius: '12px',
                  zIndex: 1
                }}
              />
              <div
                style={{
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                <table
                  style={{
                    width: '100%',
                    minWidth: '700px',
                    borderCollapse: 'collapse',
                    tableLayout: 'auto'
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={{ ...headerStyle, paddingLeft: '24px' }}>ID</th>
                      <th style={headerStyle}>Payment method</th>
                      <th style={headerStyle}>Amount</th>
                      <th style={headerStyle}>Direction</th>
                      <th style={headerStyle}>Status</th>
                      <th style={headerStyle}>Description</th>
                      <th style={headerStyle}>Paid on</th>
                      <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservation.paymentList.map((payment, index) => (
                      <tr
                        key={payment.id || index}
                        onClick={() => {
                          setDetailPayment(payment);
                          setShowDetailsModal(true);
                        }}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.15s',
                          backgroundColor:
                            payment.direction === 'out'
                              ? '#fffbeb'
                              : payment.paymentAmount <= 0
                              ? '#f9fafb'
                              : 'white'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            payment.direction === 'out'
                              ? '#fffbeb'
                              : payment.paymentAmount <= 0
                              ? '#f9fafb'
                              : 'white';
                        }}
                      >
                        {/* ID */}
                        <td
                          style={{
                            ...cellStyle,
                            paddingLeft: '24px',
                            fontWeight: 500,
                            color: '#374151',
                            maxWidth: '120px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {payment.id?.length > 10
                            ? payment.id.substring(0, 10) + '...'
                            : payment.id}
                        </td>

                        {/* Payment method */}
                        <td style={{ ...cellStyle, fontWeight: 600, color: '#111827' }}>
                          {getPaymentMethod(payment)}
                        </td>

                        {/* Amount */}
                        <td
                          style={{
                            ...cellStyle,
                            fontWeight: 500,
                            color: payment.paymentAmount <= 0 ? '#EF4444' : '#111827'
                          }}
                        >
                          <NumberFormat
                            value={payment.paymentAmount || 0}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={payment.currency ? CURRENCY[payment.currency] + ' ' : 'EGP '}
                          />
                        </td>

                        {/* Direction */}
                        <td style={cellStyle}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 12px',
                              borderRadius: '9999px',
                              fontSize: '13px',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              backgroundColor: payment.direction === 'out' ? '#fffbeb' : '#ecfdf3',
                              color: payment.direction === 'out' ? '#D97706' : '#06A458',
                              border: `1px solid ${
                                payment.direction === 'out' ? '#fde68a' : '#a6f4c5'
                              }`
                            }}
                          >
                            {payment.direction === 'out' ? 'PAY-OUT' : 'PAY-IN'}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={cellStyle}>
                          <StatusBadge status={payment.status} />
                        </td>

                        {/* Description */}
                        <td
                          style={{
                            ...cellStyle,
                            color: '#374151',
                            maxWidth: '160px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {getDescription(payment)}
                        </td>

                        {/* Paid on */}
                        <td style={{ ...cellStyle, color: '#374151' }}>{getPaidDate(payment)}</td>

                        {/* Action */}
                        <td
                          style={{ ...cellStyle, textAlign: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionDropdown
                            payment={payment}
                            isPermited={isPermited}
                            reservationSource={reservation.source}
                            isCheckingPaymobStatus={checkingPaymobPaymentId === payment.id}
                            onSend={() => {
                              setActivePaymentId(payment.id);
                              setShowSendPaymentLinkModal(true);
                            }}
                            onPartial={() => {
                              setActivePayment(payment);
                              setActivePaymentId(payment.id);
                              setShowAddPartialPaymentModal(true);
                            }}
                            onSplitPayment={() => {
                              setActivePaymentId(payment.id);
                              setShowSplitPaymentModal(true);
                            }}
                            onGenerateLink={() => {
                              setActivePaymentId(payment.id);
                              setShowGeneratePaymentLinkModal(true);
                            }}
                            onAddReceipt={() => {
                              setActivePayment(payment);
                              setShowAddPaymentRecipetModal(true);
                            }}
                            onViewHistory={() => {
                              setActivePayment(payment);
                              setShowPaymentHistoryModal(true);
                            }}
                            onCheckPaymobStatus={() => {
                              setCheckingPaymobPaymentId(payment.id);
                              checkPaymobPaymentStatus(payment.id)
                                .then((response) => {
                                  if (response?.paymobTransaction?.success === true) {
                                    birdToast.success('Payment paid');
                                  } else if (
                                    response?.paymobTransaction?.success === false ||
                                    response == null
                                  ) {
                                    birdToast.error('Payment not paid');
                                  } else {
                                    birdToast.error(
                                      'Failed to check payment status against Paymob, please contact support'
                                    );
                                  }
                                })
                                .finally(() => setCheckingPaymobPaymentId(''));
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center text-sm rounded-xl text-gray-500 border border-dashed border-neutral-[#e5e7eb]">
            No payments found
          </div>
        )}
      </div>
    </>
  );
};
